import React, { useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { BookingSummaryCard } from '../../../pages/MyTickets';
import PaymentAndRefundCard from '../../PaymentAndRefundCard';
import SupplierStatusPanel, { SupplierStatusBadge } from './SupplierStatusPanel';
import CommercialPanel from './CommercialPanel';
import CommissionAccountingPanel from './CommissionAccountingPanel';
import RefundLedgerPanel from './RefundLedgerPanel';
import DocumentsPanel from './DocumentsPanel';
import BookingTransactionsPanel from './BookingTransactionsPanel';
import ManualAmendmentPanel from './ManualAmendmentPanel';
import AmendmentModeMatrix from './AmendmentModeMatrix';
import { getUserFacingBookingId } from '../../../utils/userFacingBookingId';
import {
  BookingReferencesPanel,
  StatusOverviewPanel,
  FlightItineraryPanel,
  PassengersDetailPanel,
  SSRDetailPanel,
  PaymentDetailPanel,
  GstInvoicePanel,
  CustomerSummaryPanel,
  bookingForSsrView,
} from './HubDetailSections';

const TABS = [
  'Overview',
  'Flight',
  'Passengers',
  'SSR',
  'Payment',
  'Pricing',
  'GST',
  'Status',
  'Documents',
  'Manual ops',
];

export default function BookingDetailDrawer({ hub, open, onClose, onHubRefresh, children }) {
  const [tab, setTab] = useState('Overview');
  if (!open || !hub) return null;
  const booking = hub.booking || {};
  const bid = hub.keys?.canonical_id || booking.booking_id || booking.id;
  const tickets = hub.linked?.support_tickets || [];
  const ssrBooking = bookingForSsrView(booking, hub.ssr, hub.commercial);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-testid="flight-booking-drawer">
      <div className="bg-white w-full max-w-6xl h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-start justify-between gap-2 z-10">
          <div>
            <p className="text-[10px] text-gray-500 uppercase">Flight booking</p>
            <p className="font-mono font-bold text-[#1B375B]">{getUserFacingBookingId(booking)}</p>
            <p className="text-xs text-gray-600 mt-0.5">
              {hub.summary?.route} · {hub.summary?.pax_count} pax · PNR {hub.summary?.pnr || '—'}
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              <SupplierStatusBadge supplierStatus={hub.supplier_status} />
              <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-semibold">
                ACF: {hub.summary?.acf_status}
                {hub.summary?.acf_status_stale && hub.summary?.acf_booking_status && (
                  <span className="text-amber-700 ml-1" title="Stored status lags supplier">
                    (stale → {hub.summary.acf_booking_status})
                  </span>
                )}
              </span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex gap-1 px-4 pt-2 border-b overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-t-lg whitespace-nowrap ${
                tab === t ? 'bg-[#1B375B] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-4 flex-1">
          <div className="flex flex-wrap gap-2 text-xs">
            <a href={`/admin/ops-monitor?focus=${encodeURIComponent(bid)}`} className="inline-flex items-center gap-1 px-2 py-1 border rounded-lg text-[#1B375B] font-semibold hover:bg-gray-50">
              <ExternalLink className="w-3 h-3" /> Live Monitor
            </a>
            <a href={`/admin/refunds`} className="inline-flex items-center gap-1 px-2 py-1 border rounded-lg text-[#1B375B] font-semibold hover:bg-gray-50">
              Refund Tracker
            </a>
            {tickets[0]?.reference && (
              <a href={`/admin/support/${tickets[0].reference}`} className="inline-flex items-center gap-1 px-2 py-1 border rounded-lg text-[#1B375B] font-semibold hover:bg-gray-50">
                Support ({tickets.length})
              </a>
            )}
          </div>

          {tab === 'Overview' && (
            <>
              <CustomerSummaryPanel summary={hub.summary} />
              <BookingReferencesPanel references={hub.references} />
              <section>
                <h3 className="text-sm font-bold text-[#1B375B] mb-2">Payment &amp; transactions</h3>
                <BookingTransactionsPanel
                  transactions={hub.transactions}
                  payment={hub.payment}
                  commercial={hub.commercial}
                  booking={booking}
                />
              </section>
              <section>
                <h3 className="text-sm font-bold text-[#1B375B] mb-2">SSR &amp; add-ons</h3>
                <SSRDetailPanel ssr={hub.ssr} booking={ssrBooking} commercial={hub.commercial} />
              </section>
              <BookingSummaryCard
                booking={{ ...booking, passengers: hub.passengers?.length ? hub.passengers : booking.passengers }}
                bookingId={bid}
                isAdmin
              />
              {children}
            </>
          )}
          {tab === 'Flight' && <FlightItineraryPanel itinerary={hub.itinerary} />}
          {tab === 'Passengers' && <PassengersDetailPanel passengers={hub.passengers} />}
          {tab === 'SSR' && <SSRDetailPanel ssr={hub.ssr} booking={ssrBooking} commercial={hub.commercial} />}
          {tab === 'Payment' && (
            <>
              <BookingTransactionsPanel
                transactions={hub.transactions}
                payment={hub.payment}
                commercial={hub.commercial}
                booking={booking}
              />
              <PaymentDetailPanel payment={hub.payment} />
              {hub.refund_ledger && <RefundLedgerPanel ledger={hub.refund_ledger} />}
              {hub.cancellation?.cancellation_details?.refund_status
                && hub.cancellation.cancellation_details.refund_status !== 'no_refund' && (
                <PaymentAndRefundCard booking={booking} testIdPrefix="hub-flight" />
              )}
            </>
          )}
          {tab === 'Pricing' && (
            <>
              <CommercialPanel commercial={hub.commercial} />
              <CommissionAccountingPanel
                bookingId={bid}
                commission={hub.commission}
                onRefresh={onHubRefresh}
              />
              <SSRDetailPanel ssr={hub.ssr} booking={ssrBooking} commercial={hub.commercial} />
            </>
          )}
          {tab === 'GST' && (
            <>
              <GstInvoicePanel gst={hub.gst} />
              {hub.refund_ledger && <RefundLedgerPanel ledger={hub.refund_ledger} />}
              <CommissionAccountingPanel
                bookingId={bid}
                commission={hub.commission}
                onRefresh={onHubRefresh}
              />
            </>
          )}
          {tab === 'Status' && (
            <>
              <StatusOverviewPanel statusOverview={hub.status_overview} />
              <SupplierStatusPanel supplierStatus={hub.supplier_status} />
            </>
          )}
          {tab === 'Documents' && <DocumentsPanel hub={hub} bookingId={bid} />}
          {tab === 'Manual ops' && (
            <>
              <AmendmentModeMatrix />
              <ManualAmendmentPanel bookingId={bid} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
